import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import RemoveIcon from '@mui/icons-material/Remove';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DataTable, FormDatePicker, FormSelect, FormTextField } from '../../../shared';
import HallAnalyticsOverview from './HallAnalyticsOverview';
import HallAuditDrawer from './HallAuditDrawer';
import HallBookingCalendar from './HallBookingCalendar';
import HallBookingViewSummary from './HallBookingViewSummary';
import HallEventGuestPhoneField from './HallEventGuestPhoneField';
import HallPickerCards from './HallPickerCards';
import HallModuleNav, {
  HALL_TAB_ITEMS,
  getHallTabIndexFromLocation,
  getHallTabPath,
} from './HallModuleNav';
import { DASHBOARD_ACCENT, getDashboardDarkSurfaceTokens, getDashboardPageShellSx } from '../../../utils/dashboardSurfaceSx';
import { formatPhoneWithDialCode, isValidPhoneForCountry, splitPhoneWithDialCode } from '../../../utils/phoneValidation';
import { detectThemeSync, watchThemeChanges } from '../../../utils/themeUtils';
import {
  createHall,
  createHallBooking,
  deleteHallBooking,
  getHallBookingDetail,
  getHallBookings,
  getHallDashboard,
  getHalls,
  updateHallBooking,
} from '../api';
import { buildHallFormData, flatFormToHallShape, MAX_HALL_IMAGES } from '../utils/hallPayload';
import {
  BUSINESS_TYPE_OPTIONS,
  getBusinessTypeLabel,
  getHallDefaultAmount,
  getHallFullDayAmount,
  getHallHourlyAmount,
  isHallBookable,
} from '../utils/normalizeHall';
import {
  buildHallEventBookingNotes,
  inferHallEventPaymentType,
  parseHallEventBookingNotes,
} from '../utils/hallEventForm';
import {
  collectTimelineBookings,
  findFirstAvailableHall,
  findHallBookingConflict,
  formatHallOverlapErrorMessage,
} from '../utils/hallBookingOverlap';
import HallEventPaymentFields, {
  applyHallEventPaymentTypeChange,
  syncHallEventAdvanceWithAmount,
} from './HallEventPaymentFields';
import HallManagementWorkspace from './HallManagementWorkspace';
import HallVendorManagementWorkspace from './HallVendorManagementWorkspace';
import {
  HALL_DRAWER_FIELD_GAP,
  HALL_SIDE_DRAWER_WIDTH_SX,
  getHallDatePickerSlotProps,
  getHallDrawerFormGridSx,
  getHallDrawerPaperSx,
  getHallFormInputSx,
  getHallFormSectionTitleSx,
} from '../utils/hallDrawerStyles';

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

const HALL_ACCENT = '#4ce1b6';
const SECONDARY = '#8ac9ff';
const WARNING = '#fbbf24';
const DANGER = '#fb7185';
const PURPLE = '#c4b5fd';

const TAB_ID_TO_INDEX = HALL_TAB_ITEMS.reduce((acc, item, index) => {
  acc[item.id] = index;
  return acc;
}, {});

/** Calendar rows send hall name in `hall` and numeric id in `hall_id`; the form select needs the id. */
function resolveHallBookingHallId(event, halls = [], fallbackHallId = '') {
  if (event?.hall_id != null && event.hall_id !== '') {
    return String(event.hall_id);
  }
  const rawHall = event?.hall;
  if (rawHall != null && rawHall !== '') {
    const asString = String(rawHall);
    if (/^\d+$/.test(asString)) {
      return asString;
    }
    const matchedHall = halls.find((hall) => hall.name === asString);
    if (matchedHall?.id != null) {
      return String(matchedHall.id);
    }
  }
  if (fallbackHallId != null && fallbackHallId !== '') {
    return String(fallbackHallId);
  }
  return '';
}

const defaultHallForm = {
  name: '',
  code: '',
  capacity: '',
  description: '',
  manager_name: '',
  phone: '',
  email: '',
  business_types: ['marriage_hall'],
  banquet_style_capacity: '',
  theatre_style_capacity: '',
  dining_capacity: '',
  floor_area: '',
  floor: '',
  hourly_rate: '',
  full_day_rate: '',
  is_24_hours: false,
  open_time: '09:00',
  close_time: '23:00',
  hall_image_files: [],
};

const DEFAULT_HOURLY_DURATION_HOURS = 4;
const MIN_HOURLY_DURATION_HOURS = 1;
const MAX_HOURLY_DURATION_HOURS = 12;
const EVENT_DELETE_CONFIRM_WORD = 'confirm';

const defaultBookingForm = {
  hall_id: '',
  event_id: '',
  event_name: '',
  event_type: 'wedding',
  customer_name: '',
  customer_phone: '',
  customer_phone_country_code: '+91',
  customer_email: '',
  event_date: '',
  end_date: '',
  booking_type: 'hourly',
  start_time: '',
  end_time: '',
  duration_hours: DEFAULT_HOURLY_DURATION_HOURS,
  duration_minutes: 0,
  guests: '',
  adults: '',
  children: '',
  amount: '',
  advance_amount: '',
  payment_type: 'full',
  payment_mode: 'cash',
  status: 'tentative',
  notes: '',
};

function computeEndFromDuration(eventDate, startTime, hours = DEFAULT_HOURLY_DURATION_HOURS, minutes = 0) {
  const start = moment(`${eventDate || moment().format('YYYY-MM-DD')} ${startTime || '10:00'}`, 'YYYY-MM-DD HH:mm');
  if (!start.isValid()) {
    return { end_date: eventDate || '', end_time: getRangeEndForSlot(startTime || '10:00') };
  }
  const end = start.clone().add(Number(hours) || 0, 'hours').add(Number(minutes) || 0, 'minutes');
  return {
    end_date: end.format('YYYY-MM-DD'),
    end_time: end.format('HH:mm'),
  };
}

function durationFromStartEnd(eventDate, endDate, startTime, endTime) {
  const start = moment(`${eventDate} ${startTime}`, 'YYYY-MM-DD HH:mm');
  const end = moment(`${endDate || eventDate} ${endTime}`, 'YYYY-MM-DD HH:mm');
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
    return { duration_hours: DEFAULT_HOURLY_DURATION_HOURS, duration_minutes: 0 };
  }
  const totalMinutes = end.diff(start, 'minutes');
  return {
    duration_hours: Math.floor(totalMinutes / 60),
    duration_minutes: totalMinutes % 60,
  };
}

function computeHourlyAmount(hall, hours = DEFAULT_HOURLY_DURATION_HOURS, minutes = 0) {
  const rate = getHallHourlyAmount(hall);
  const totalHours = (Number(hours) || 0) + ((Number(minutes) || 0) / 60);
  if (!rate || totalHours <= 0) return rate ? String(Math.round(rate)) : '';
  return String(Math.round(rate * totalHours));
}

const FULL_DAY_DEFAULT_START_TIME = '10:00';
const FULL_DAY_DEFAULT_END_TIME = '10:00';

/** Full-day blocks are 24h units (e.g. 10:00 → next day 10:00 = 1 day). */
function countFullDayBlocks(startDate, endDate) {
  const start = moment(startDate, 'YYYY-MM-DD');
  const end = moment(endDate || startDate, 'YYYY-MM-DD');
  if (!start.isValid() || !end.isValid()) return 1;
  return Math.max(1, end.diff(start, 'days'));
}

function computeFullDayAmount(hall, startDate, endDate) {
  const rate = getHallFullDayAmount(hall) || getHallDefaultAmount(hall);
  if (!rate) return '';
  return String(Math.round(rate * countFullDayBlocks(startDate, endDate)));
}

function computeBookingAmount(hall, form = {}) {
  if (!hall) return '';
  if (form.booking_type === 'day') {
    return computeFullDayAmount(hall, form.event_date, form.end_date || form.event_date);
  }
  return computeHourlyAmount(
    hall,
    form.duration_hours ?? DEFAULT_HOURLY_DURATION_HOURS,
    form.duration_minutes ?? 0,
  );
}

const numberFormat = new Intl.NumberFormat('en-IN');
const currencyFormat = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

function formatCurrency(value) {
  return currencyFormat.format(Number(value || 0));
}

function getBookingAmountHelperText(hall, form = {}) {
  if (!hall) return 'Select a hall to auto-calculate amount.';
  if (form.booking_type === 'day') {
    const rate = getHallFullDayAmount(hall) || getHallDefaultAmount(hall);
    if (!rate) return 'Set full-day rate on the hall to auto-calculate.';
    const days = countFullDayBlocks(form.event_date, form.end_date || form.event_date);
    return `Auto ${formatCurrency(rate)}/day × ${days} day${days === 1 ? '' : 's'} · editable`;
  }
  const rate = getHallHourlyAmount(hall);
  const hours = Number(form.duration_hours ?? DEFAULT_HOURLY_DURATION_HOURS) || DEFAULT_HOURLY_DURATION_HOURS;
  if (!rate) return 'Set hourly rate on the hall to auto-calculate.';
  return `Auto ${formatCurrency(rate)}/hr × ${hours}h · editable`;
}

function withAutoBookingAmount(prev, patch, hall) {
  const next = { ...prev, ...patch };
  const computed = computeBookingAmount(hall, next);
  if (computed !== '' && computed != null) {
    next.amount = computed;
  }
  return syncHallEventAdvanceWithAmount(next);
}

function normalizeChartRows(rows = []) {
  return (rows || []).map((row) => ({
    ...row,
    name: row.name || row.label || row.date || '—',
  }));
}

function getNextSlotTime(hourLabel) {
  const [hour = '0'] = String(hourLabel || '00:00').split(':');
  const nextHour = Number(hour) + 1;
  if (nextHour >= 24) return '23:59';
  return `${String(nextHour).padStart(2, '0')}:00`;
}

function timeToMinutes(value) {
  const [hours = '0', minutes = '0'] = String(value || '00:00').split(':');
  return Number(hours) * 60 + Number(minutes);
}

function getRangeEndForSlot(slotHour) {
  return getNextSlotTime(slotHour);
}

function isBookingPhoneValid(phone, countryCode) {
  return isValidPhoneForCountry(phone, countryCode || '+91', {
    required: true,
    label: 'Phone',
  });
}

function renderEventIdLink(event, navigate, sx = {}) {
  const eventId = event.event_id || event.booking_code || event.booking_id || `EVT-${event.id}`;
  return (
    <Typography
      component="span"
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        if (!event?.id) return;
        navigate(`/hall/event/${event.id}/`);
      }}
      onMouseDown={(clickEvent) => clickEvent.stopPropagation()}
      sx={{
        color: HALL_ACCENT,
        fontWeight: 800,
        fontSize: '0.82rem',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
        ...sx,
      }}
    >
      {eventId}
    </Typography>
  );
}

function statusChipSx(status, isDark) {
  const colors = {
    confirmed: HALL_ACCENT,
    tentative: WARNING,
    completed: SECONDARY,
    cancelled: DANGER,
    available: HALL_ACCENT,
    occupied: WARNING,
    inactive: '#94a3b8',
  };
  const color = colors[String(status || '').toLowerCase()] || SECONDARY;
  return {
    height: 24,
    fontWeight: 800,
    textTransform: 'capitalize',
    color,
    bgcolor: alpha(color, isDark ? 0.16 : 0.12),
    border: `1px solid ${alpha(color, 0.28)}`,
  };
}

function AddHallDrawer({ open, onClose, onCreate, saving, isDark }) {
  const [form, setForm] = useState(defaultHallForm);
  const [imagePreviews, setImagePreviews] = useState([]);

  useEffect(() => {
    setForm(defaultHallForm);
    setImagePreviews((prev) => {
      prev.forEach((item) => {
        if (item?.url) URL.revokeObjectURL(item.url);
      });
      return [];
    });
  }, [open]);

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const addHallImages = (fileList) => {
    const incoming = Array.from(fileList || []).filter((file) => file?.type?.startsWith('image/'));
    if (!incoming.length) return;
    setForm((prev) => {
      const existing = Array.isArray(prev.hall_image_files) ? prev.hall_image_files : [];
      const remainingSlots = Math.max(0, MAX_HALL_IMAGES - existing.length);
      const accepted = incoming.slice(0, remainingSlots);
      if (!accepted.length) return prev;
      const nextFiles = [...existing, ...accepted];
      setImagePreviews((prevPreviews) => [
        ...prevPreviews,
        ...accepted.map((file, index) => ({
          id: `local-${Date.now()}-${index}-${file.name}`,
          url: URL.createObjectURL(file),
          name: file.name,
        })),
      ]);
      return { ...prev, hall_image_files: nextFiles };
    });
  };

  const removeHallImage = (previewId) => {
    setImagePreviews((prevPreviews) => {
      const index = prevPreviews.findIndex((item) => item.id === previewId);
      if (index < 0) return prevPreviews;
      const target = prevPreviews[index];
      if (target?.url) URL.revokeObjectURL(target.url);
      setForm((prev) => ({
        ...prev,
        hall_image_files: (prev.hall_image_files || []).filter((_, idx) => idx !== index),
      }));
      return prevPreviews.filter((_, idx) => idx !== index);
    });
  };

  const submit = () => {
    const formData = buildHallFormData(flatFormToHallShape(form), form.hall_image_files || []);
    onCreate(formData);
  };

  const inputSx = getHallFormInputSx(isDark);

  const drawerPaperSx = getHallDrawerPaperSx(isDark);

  const sectionTitleSx = getHallFormSectionTitleSx();

  const renderSection = (title, children) => (
    <Stack spacing={1}>
      <Typography sx={sectionTitleSx}>{title}</Typography>
      <Grid
        container
        columnSpacing={HALL_DRAWER_FIELD_GAP}
        rowSpacing={HALL_DRAWER_FIELD_GAP}
        sx={{ width: '100%', maxWidth: '100%', m: 0 }}
      >
        {children}
      </Grid>
      <Divider sx={{ borderColor: isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08) }} />
    </Stack>
  );

  return (
    <Drawer anchor="right" open={open} onClose={saving ? undefined : onClose} PaperProps={{ sx: drawerPaperSx }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.08)}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <MapsHomeWorkIcon sx={{ color: HALL_ACCENT }} />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Add Hall</Typography>
          </Stack>
          <Button onClick={onClose} disabled={saving} sx={{ minWidth: 0, color: isDark ? '#cbd5e1' : '#475569' }}>
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 1.5, boxSizing: 'border-box' }}>
          <Stack spacing={1.5}>
            {renderSection('Basic Information', (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField label="Hall Name" value={form.name} onChange={updateField('name')} required fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Hall Code" value={form.code} onChange={updateField('code')} fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Description" value={form.description} onChange={updateField('description')} multiline minRows={2} fullWidth sx={inputSx} />
                </Grid>
              </>
            ))}

            {renderSection('Contact Information', (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField label="Manager / Contact Person" value={form.manager_name} onChange={updateField('manager_name')} fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Mobile Number" value={form.phone} onChange={updateField('phone')} fullWidth sx={inputSx} inputProps={{ inputMode: 'numeric' }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Email" value={form.email} onChange={updateField('email')} type="email" fullWidth sx={inputSx} />
                </Grid>
              </>
            ))}

            {renderSection('Business Information', (
              <>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Business Type"
                    value={form.business_types || []}
                    onChange={(event) => {
                      const next = typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value;
                      setForm((prev) => ({ ...prev, business_types: next }));
                    }}
                    fullWidth
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected || []).map((value) => getBusinessTypeLabel(value)).join(', '),
                    }}
                    sx={inputSx}
                  >
                    {BUSINESS_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={(form.business_types || []).includes(option.value)} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            ))}

            {renderSection('Capacity & Layout', (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField label="Main Capacity" value={form.capacity} onChange={updateField('capacity')} type="number" required fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Dining Capacity" value={form.dining_capacity} onChange={updateField('dining_capacity')} type="number" fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Banquet Style Capacity" value={form.banquet_style_capacity} onChange={updateField('banquet_style_capacity')} type="number" fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Theatre Style Capacity" value={form.theatre_style_capacity} onChange={updateField('theatre_style_capacity')} type="number" fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Floor Area" value={form.floor_area} onChange={updateField('floor_area')} fullWidth sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Floor / Level" value={form.floor} onChange={updateField('floor')} fullWidth sx={inputSx} />
                </Grid>
              </>
            ))}

            {renderSection('Pricing', (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField label="Hourly Rate" value={form.hourly_rate} onChange={updateField('hourly_rate')} type="number" fullWidth InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }} sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Full Day Rate" value={form.full_day_rate} onChange={updateField('full_day_rate')} type="number" fullWidth InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }} sx={inputSx} />
                </Grid>
              </>
            ))}

            {renderSection('Working Hours', (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(form.is_24_hours)}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            is_24_hours: checked,
                            open_time: checked ? '00:00' : (prev.open_time || '09:00'),
                            close_time: checked ? '23:59' : (prev.close_time || '23:00'),
                          }));
                        }}
                        sx={{ color: HALL_ACCENT, '&.Mui-checked': { color: HALL_ACCENT } }}
                      />
                    )}
                    label="24 Hours"
                  />
                </Grid>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="Open Time"
                      ampm
                      format="hh:mm A"
                      disabled={Boolean(form.is_24_hours)}
                      value={form.open_time ? moment(form.open_time, 'HH:mm') : null}
                      onChange={(nextTime) => {
                        setForm((prev) => ({
                          ...prev,
                          open_time: nextTime?.isValid?.() ? nextTime.format('HH:mm') : '',
                        }));
                      }}
                      slotProps={getHallDatePickerSlotProps(isDark, {
                        disabled: Boolean(form.is_24_hours),
                        sx: inputSx,
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="Close Time"
                      ampm
                      format="hh:mm A"
                      disabled={Boolean(form.is_24_hours)}
                      value={form.close_time ? moment(form.close_time, 'HH:mm') : null}
                      onChange={(nextTime) => {
                        setForm((prev) => ({
                          ...prev,
                          close_time: nextTime?.isValid?.() ? nextTime.format('HH:mm') : '',
                        }));
                      }}
                      slotProps={getHallDatePickerSlotProps(isDark, {
                        disabled: Boolean(form.is_24_hours),
                        sx: inputSx,
                      })}
                    />
                  </Grid>
                </LocalizationProvider>
              </>
            ))}

            {renderSection('Hall Images', (
              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  disabled={(form.hall_image_files || []).length >= MAX_HALL_IMAGES}
                  sx={{
                    width: '100%',
                    minHeight: 88,
                    borderStyle: 'dashed',
                    borderColor: alpha(HALL_ACCENT, 0.5),
                    color: HALL_ACCENT,
                    fontWeight: 900,
                  }}
                >
                  {(form.hall_image_files || []).length >= MAX_HALL_IMAGES
                    ? `Maximum ${MAX_HALL_IMAGES} images reached`
                    : 'Upload Hall Images'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    multiple
                    onChange={(event) => {
                      addHallImages(event.target.files);
                      event.target.value = '';
                    }}
                  />
                </Button>
                <Typography sx={{ mt: 1, color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.78rem', fontWeight: 700 }}>
                  {(form.hall_image_files || []).length}/{MAX_HALL_IMAGES} images · First image is used as the hall cover
                </Typography>
                {imagePreviews.length ? (
                  <Box
                    sx={{
                      mt: 1.5,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(108px, 1fr))',
                      gap: 1,
                    }}
                  >
                    {imagePreviews.map((preview, index) => (
                      <Box
                        key={preview.id}
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: `1px solid ${alpha(HALL_ACCENT, 0.25)}`,
                          aspectRatio: '1 / 1',
                        }}
                      >
                        <Box
                          component="img"
                          src={preview.url}
                          alt={preview.name || `Hall preview ${index + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        {index === 0 ? (
                          <Chip
                            size="small"
                            label="Cover"
                            sx={{
                              position: 'absolute',
                              left: 6,
                              top: 6,
                              height: 22,
                              fontWeight: 800,
                              bgcolor: alpha('#031512', 0.72),
                              color: HALL_ACCENT,
                            }}
                          />
                        ) : null}
                        <Button
                          size="small"
                          onClick={() => removeHallImage(preview.id)}
                          sx={{
                            position: 'absolute',
                            right: 4,
                            top: 4,
                            minWidth: 28,
                            width: 28,
                            height: 28,
                            p: 0,
                            borderRadius: '50%',
                            bgcolor: alpha('#0f172a', 0.72),
                            color: '#fff',
                            '&:hover': { bgcolor: alpha('#fb7185', 0.9) },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </Button>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Grid>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            p: 1.5,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.08)}`,
            boxSizing: 'border-box',
          }}
        >
          <Button variant="outlined" onClick={onClose} disabled={saving} sx={{ borderColor: isDark ? alpha('#ffffff', 0.14) : alpha('#0f172a', 0.16), color: isDark ? '#e2e8f0' : '#334155', fontWeight: 900 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving || !form.name.trim() || !Number(form.capacity || 0)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ bgcolor: HALL_ACCENT, color: '#031512', fontWeight: 900, '&:hover': { bgcolor: '#3bd1a7' } }}
          >
            Save Hall
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export default function HallDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [themeMode, setThemeMode] = useState(() => detectThemeSync());
  const isDark = themeMode === 'dark';
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  const [tab, setTab] = useState(() => getHallTabIndexFromLocation(location.pathname, location.search));
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedHallId, setSelectedHallId] = useState('');
  const [calendarHallFilter, setCalendarHallFilter] = useState('');
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [savingHall, setSavingHall] = useState(false);
  const [activeSlotHour, setActiveSlotHour] = useState('');
  const [bookingDrawerOpen, setBookingDrawerOpen] = useState(false);
  const [bookingDrawerMode, setBookingDrawerMode] = useState('create');
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [eventAuditDrawerOpen, setEventAuditDrawerOpen] = useState(false);
  const [eventAuditEvents, setEventAuditEvents] = useState([]);
  const [eventAuditLoading, setEventAuditLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState(defaultBookingForm);
  const [savingBooking, setSavingBooking] = useState(false);
  const [deletingBookingId, setDeletingBookingId] = useState(null);
  const [deleteEventDialog, setDeleteEventDialog] = useState({ open: false, event: null });
  const [deleteEventConfirmInput, setDeleteEventConfirmInput] = useState('');
  const bookingDrawerScrollRef = useRef(null);
  const guestLinkedCustomerRef = useRef(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventRows, setEventRows] = useState([]);
  const [eventsCount, setEventsCount] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPerPage, setEventsPerPage] = useState(10);
  const [eventsFilters, setEventsFilters] = useState({
    event_id: '',
    search: '',
    hall_id: '',
    date: '',
    status: '',
  });
  const [hallDayBookings, setHallDayBookings] = useState([]);
  const [allDayBookings, setAllDayBookings] = useState([]);
  const [managementHalls, setManagementHalls] = useState([]);
  const [managementHallsLoading, setManagementHallsLoading] = useState(false);
  const [managementHallsLoaded, setManagementHallsLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [analyticsStartDate, setAnalyticsStartDate] = useState(() => moment().startOf('month'));
  const [analyticsEndDate, setAnalyticsEndDate] = useState(() => moment().endOf('month'));
  const [analyticsPreset, setAnalyticsPreset] = useState('this_month');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPdfExporting, setAnalyticsPdfExporting] = useState(false);
  const [analyticsPdfCaptureMode, setAnalyticsPdfCaptureMode] = useState(false);
  const analyticsInsightsRef = useRef(null);
  const analyticsExportInProgressRef = useRef(false);
  const analyticsRangeRef = useRef({
    start: moment().startOf('month'),
    end: moment().endOf('month'),
  });

  useEffect(() => {
    analyticsRangeRef.current = {
      start: analyticsStartDate,
      end: analyticsEndDate,
    };
  }, [analyticsStartDate, analyticsEndDate]);

  useEffect(() => watchThemeChanges(setThemeMode), []);

  useEffect(() => {
    const normalizedPath = String(location.pathname || '').replace(/\/+$/, '') || '/hall';
    if (normalizedPath === '/hall') {
      const queryTabId = new URLSearchParams(location.search).get('tab');
      const nextTabId = (queryTabId && TAB_ID_TO_INDEX[queryTabId] != null)
        ? queryTabId
        : 'dashboard';
      navigate(getHallTabPath(nextTabId), { replace: true });
      return;
    }
    setTab(getHallTabIndexFromLocation(location.pathname, location.search));
  }, [location.pathname, location.search, navigate]);

  const handleTabChange = (nextTab) => {
    const tabItem = HALL_TAB_ITEMS[nextTab];
    if (!tabItem || tabItem.disabled) return;
    setTab(nextTab);
    navigate(getHallTabPath(tabItem.id));
  };

  const showMessage = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  const closeBookingDrawer = () => {
    setBookingDrawerOpen(false);
    setBookingDrawerMode('create');
    setEditingBookingId(null);
    setEventAuditDrawerOpen(false);
    setEventAuditEvents([]);
    setEventAuditLoading(false);
    setActiveSlotHour('');
    guestLinkedCustomerRef.current = null;
    setBookingForm(defaultBookingForm);
  };

  const loadEventAudit = useCallback(async (bookingId) => {
    if (!bookingId) {
      setEventAuditEvents([]);
      return;
    }
    setEventAuditLoading(true);
    try {
      const payload = await getHallBookingDetail(bookingId);
      const activity = Array.isArray(payload?.activity)
        ? payload.activity
        : Array.isArray(payload?.audit_logs)
          ? payload.audit_logs.map((event) => ({
            id: event.id,
            type: event.event_type,
            title: event.title,
            description: event.description,
            actor: event.actor || 'System',
            created_at: event.created_at,
          }))
          : [];
      setEventAuditEvents(activity);
    } catch (err) {
      setEventAuditEvents([]);
      showMessage(
        'error',
        err?.response?.data?.error || err?.message || 'Failed to load event audit.',
      );
    } finally {
      setEventAuditLoading(false);
    }
  }, []);

  const openEventAuditDrawer = () => {
    if (!editingBookingId) return;
    setEventAuditDrawerOpen(true);
    loadEventAudit(editingBookingId);
  };

  const clearGuestLinkedCustomer = () => {
    guestLinkedCustomerRef.current = null;
  };

  const handleCustomerPhoneChange = (nextPhone) => {
    setBookingForm((prev) => {
      const digitsOnly = String(nextPhone || '').replace(/\D/g, '');
      if (!digitsOnly && guestLinkedCustomerRef.current) {
        guestLinkedCustomerRef.current = null;
        return {
          ...prev,
          customer_phone: nextPhone,
          customer_name: '',
          customer_email: '',
        };
      }
      return { ...prev, customer_phone: nextPhone };
    });
  };

  const handleGuestSelectFromPhone = ({ name, email, guest }) => {
    const guestId = Number(guest?.id);
    guestLinkedCustomerRef.current = {
      id: Number.isInteger(guestId) && guestId > 0 ? guestId : null,
      name: name || '',
      email: email || '',
    };
    setBookingForm((prev) => ({
      ...prev,
      customer_name: name || prev.customer_name,
      customer_email: email || prev.customer_email,
    }));
  };

  const handleCustomerNameChange = (event) => {
    clearGuestLinkedCustomer();
    updateBookingField('customer_name')(event);
  };

  const handleCustomerEmailChange = (event) => {
    clearGuestLinkedCustomer();
    updateBookingField('customer_email')(event);
  };

  const formatHallSaveError = (err) => {
    const message = err?.userMessage || err?.message || 'Failed to save hall';
    return message
      .replace(/_/g, ' ')
      .replace(/Value must be valid JSON\.?/gi, 'Please check the highlighted section details.')
      .replace(/;/g, '\n');
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { start, end } = analyticsRangeRef.current;
      const payload = await getHallDashboard({
        date: selectedDate,
        analytics_start: start.format('YYYY-MM-DD'),
        analytics_end: end.format('YYYY-MM-DD'),
      });
      setData(payload);
      if (payload?.selected_hall_id) {
        setSelectedHallId((current) => current || String(payload.selected_hall_id));
      }
    } catch (err) {
      setError(err?.userMessage || err?.message || 'Unable to load Hall Booking dashboard.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fetchAnalyticsInsights = useCallback(async (start, end) => {
    if (!start?.isValid?.() || !end?.isValid?.()) return;
    setAnalyticsLoading(true);
    try {
      const payload = await getHallDashboard({
        date: selectedDate,
        analytics_start: start.format('YYYY-MM-DD'),
        analytics_end: end.format('YYYY-MM-DD'),
      });
      setData((prev) => ({
        ...(prev || {}),
        analytics: payload.analytics,
      }));
    } catch (err) {
      showMessage('error', err?.userMessage || err?.message || 'Unable to refresh analytics insights.');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [selectedDate]);

  const handleAnalyticsDateRangeChange = (start, end, preset) => {
    setAnalyticsStartDate(start);
    setAnalyticsEndDate(end);
    setAnalyticsPreset(preset || 'custom');
    fetchAnalyticsInsights(start, end);
  };

  const handleAnalyticsRefresh = () => {
    fetchAnalyticsInsights(analyticsStartDate, analyticsEndDate);
  };

  const handleAnalyticsPdfDownload = async () => {
    const analytics = data?.analytics;
    if (
      !analytics
      || analyticsPdfExporting
      || analyticsExportInProgressRef.current
      || !analyticsInsightsRef.current
    ) {
      return;
    }

    analyticsExportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setAnalyticsPdfExporting(true);

    try {
      const rangeLabel = `${moment(analytics.start_date).format('DD MMM YYYY')} - ${moment(analytics.end_date).format('DD MMM YYYY')}`;
      const filename = `hall-analytics-${moment().format('YYYYMMDD-HHmm')}.pdf`;

      setAnalyticsPdfCaptureMode(true);
      await new Promise((resolve) => setTimeout(resolve, 80));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(resolve))));
      await new Promise((resolve) => setTimeout(resolve, 750));
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const canvas = await html2canvas(analyticsInsightsRef.current, {
        backgroundColor: isDark ? '#0f172a' : '#f2f4f7',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: analyticsInsightsRef.current.scrollWidth,
        windowHeight: analyticsInsightsRef.current.scrollHeight,
        onclone: (_documentClone, clonedElement) => {
          clonedElement.style.background = isDark
            ? 'linear-gradient(145deg, #0b1524 0%, #0f172a 100%)'
            : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)';
          clonedElement.style.width = '1200px';
          clonedElement.style.padding = '24px';
          clonedElement.style.boxSizing = 'border-box';
          clonedElement.style.color = isDark ? '#f8fafc' : '#0f172a';
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const drawPdfHeader = (pageNumber) => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Hall Analytics Insights', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          `Range: ${rangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageNumber}`,
          pageWidth / 2,
          23.6,
          { align: 'center' },
        );
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };

      const drawPdfFooter = (pageNumber) => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Professional hall booking intelligence for smarter operations.', margin, footerTop + 9.2);
        pdf.text(`Page ${pageNumber}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };

      const imageData = canvas.toDataURL('image/png');
      const imageWidth = maxImageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      const imageLeft = (pageWidth - imageWidth) / 2;
      let renderedHeight = 0;
      while (renderedHeight < imageHeight) {
        drawPdfHeader(pageIndex);
        pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
        drawPdfFooter(pageIndex);
        renderedHeight += printableHeight;
        if (renderedHeight < imageHeight) {
          pdf.addPage();
          pageIndex += 1;
        }
      }

      pdf.save(filename);
    } catch (err) {
      showMessage('error', err?.userMessage || err?.message || 'Unable to export analytics PDF.');
    } finally {
      setAnalyticsPdfCaptureMode(false);
      document.body.classList.remove('dashboard-pdf-exporting');
      setAnalyticsPdfExporting(false);
      analyticsExportInProgressRef.current = false;
    }
  };

  const loadEventRows = useCallback(async () => {
    setEventsLoading(true);
    try {
      const payload = await getHallBookings({
        page: eventsPage,
        page_size: eventsPerPage,
        event_id: eventsFilters.event_id || undefined,
        search: eventsFilters.search || undefined,
        hall_id: eventsFilters.hall_id || undefined,
        date: eventsFilters.date || undefined,
        status: eventsFilters.status || undefined,
      });
      setEventRows(payload?.results || []);
      setEventsCount(Number(payload?.count || 0));
    } catch (err) {
      showMessage('error', err?.userMessage || err?.message || 'Unable to load hall events.');
    } finally {
      setEventsLoading(false);
    }
  }, [eventsPage, eventsPerPage, eventsFilters.event_id, eventsFilters.search, eventsFilters.hall_id, eventsFilters.date, eventsFilters.status]);

  const loadManagementHalls = useCallback(async () => {
    setManagementHallsLoading(true);
    try {
      const payload = await getHalls({ date: selectedDate });
      setManagementHalls(payload?.results || []);
      setManagementHallsLoaded(true);
    } catch (err) {
      showMessage('error', err?.userMessage || err?.message || 'Unable to load halls.');
    } finally {
      setManagementHallsLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (tab !== 2) return;
    loadEventRows();
  }, [tab, loadEventRows]);

  useEffect(() => {
    if (tab !== 3) return;
    loadManagementHalls();
  }, [tab, loadManagementHalls]);


  useEffect(() => {
    if (!bookingDrawerOpen) return;
    if (bookingDrawerScrollRef.current) {
      bookingDrawerScrollRef.current.scrollTop = 0;
    }
  }, [bookingDrawerOpen, activeSlotHour]);

  useEffect(() => {
    if (!bookingDrawerOpen || !bookingForm.hall_id || !bookingForm.event_date) {
      setHallDayBookings([]);
      return undefined;
    }

    const rangeStart = bookingForm.event_date;
    const rangeEnd = bookingForm.end_date || bookingForm.event_date;
    let cancelled = false;
    (async () => {
      try {
        const payload = await getHallBookings({
          hall_id: bookingForm.hall_id,
          start_date: rangeStart,
          end_date: rangeEnd,
          page_size: 200,
        });
        if (!cancelled) {
          setHallDayBookings(payload?.results || []);
        }
      } catch {
        if (!cancelled) {
          setHallDayBookings([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingDrawerOpen, bookingForm.hall_id, bookingForm.event_date, bookingForm.end_date]);

  const loadDayBookings = useCallback(async (eventDate, endDate = null) => {
    try {
      const payload = await getHallBookings({
        start_date: eventDate,
        end_date: endDate || eventDate,
        page_size: 200,
      });
      return payload?.results || [];
    } catch {
      return collectTimelineBookings(data?.timeline);
    }
  }, [data?.timeline]);

  useEffect(() => {
    if (!bookingDrawerOpen || !bookingForm.event_date) {
      setAllDayBookings([]);
      return undefined;
    }

    let cancelled = false;
    loadDayBookings(bookingForm.event_date, bookingForm.end_date || bookingForm.event_date).then((bookings) => {
      if (!cancelled) {
        setAllDayBookings(bookings);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [bookingDrawerOpen, bookingForm.event_date, bookingForm.end_date, loadDayBookings]);

  const halls = data?.halls || [];
  const bookableHalls = halls.filter(isHallBookable);
  const selectedHall = bookableHalls.find((hall) => String(hall.id) === String(selectedHallId)) || bookableHalls[0] || null;

  const getBookingsForOverlapCheck = useCallback((hallId, eventDate) => {
    const normalizedHallId = String(hallId || '');
    const normalizedDate = String(eventDate || '').slice(0, 10);
    if (
      String(bookingForm.hall_id) === normalizedHallId
      && String(bookingForm.event_date || '').slice(0, 10) === normalizedDate
      && hallDayBookings.length
    ) {
      return hallDayBookings;
    }
    if (
      String(selectedHall?.id) === normalizedHallId
      && String(selectedDate).slice(0, 10) === normalizedDate
    ) {
      return collectTimelineBookings(data?.timeline);
    }
    return hallDayBookings;
  }, [
    bookingForm.event_date,
    bookingForm.hall_id,
    data?.timeline,
    hallDayBookings,
    selectedDate,
    selectedHall?.id,
  ]);

  const resolveOverlapHallName = useCallback((hallId) => {
    return halls.find((hall) => String(hall.id) === String(hallId))?.name
      || selectedHall?.name
      || 'This hall';
  }, [halls, selectedHall?.name]);

  const checkBookingTimeConflict = useCallback(({
    hallId,
    eventDate,
    endDate = null,
    startTime,
    endTime,
    excludeBookingId = null,
    bookings = null,
  }) => {
    if (!hallId || !eventDate || !startTime || !endTime) {
      return null;
    }
    const sourceBookings = bookings || getBookingsForOverlapCheck(hallId, eventDate);
    return findHallBookingConflict({
      bookings: sourceBookings,
      hallId,
      eventDate,
      endDate: endDate || eventDate,
      startTime,
      endTime,
      excludeBookingId,
    });
  }, [getBookingsForOverlapCheck]);

  const bookingOverlapConflict = useMemo(() => {
    if (bookingDrawerMode === 'view') return null;
    const sourceBookings = (
      hallDayBookings.length
        ? hallDayBookings
        : (allDayBookings.length ? allDayBookings : collectTimelineBookings(data?.timeline))
    );
    return checkBookingTimeConflict({
      hallId: bookingForm.hall_id,
      eventDate: bookingForm.event_date,
      endDate: bookingForm.end_date || bookingForm.event_date,
      startTime: bookingForm.start_time,
      endTime: bookingForm.end_time,
      excludeBookingId: editingBookingId,
      bookings: sourceBookings,
    });
  }, [
    allDayBookings,
    bookingDrawerMode,
    bookingForm.end_date,
    bookingForm.end_time,
    bookingForm.event_date,
    bookingForm.hall_id,
    bookingForm.start_time,
    checkBookingTimeConflict,
    data?.timeline,
    editingBookingId,
    hallDayBookings,
  ]);

  const bookingOverlapMessage = bookingOverlapConflict
    ? formatHallOverlapErrorMessage(bookingOverlapConflict, resolveOverlapHallName(bookingForm.hall_id))
    : '';

  const alternateHallForSlot = useMemo(() => {
    if (!bookingOverlapConflict || bookingDrawerMode === 'view') {
      return null;
    }
    const bookingsSource = allDayBookings.length ? allDayBookings : hallDayBookings;
    return findFirstAvailableHall({
      halls: bookableHalls,
      bookings: bookingsSource,
      eventDate: bookingForm.event_date,
      endDate: bookingForm.end_date || bookingForm.event_date,
      startTime: bookingForm.start_time,
      endTime: bookingForm.end_time,
      busyHallId: bookingForm.hall_id,
      excludeBookingId: editingBookingId,
      isHallBookable,
    });
  }, [
    allDayBookings,
    bookableHalls,
    bookingDrawerMode,
    bookingForm.end_date,
    bookingForm.end_time,
    bookingForm.event_date,
    bookingForm.hall_id,
    bookingForm.start_time,
    bookingOverlapConflict,
    editingBookingId,
    hallDayBookings,
  ]);

  const switchBookingToHall = (hall) => {
    if (!hall) return;
    setBookingForm((prev) => withAutoBookingAmount(prev, {
      hall_id: String(hall.id),
      guests: prev.guests || hall.capacity || '',
      adults: prev.adults || hall.capacity || '',
    }, hall));
  };

  const resolveBookingHall = (hallId) => (
    bookableHalls.find((item) => String(item.id) === String(hallId))
    || halls.find((item) => String(item.id) === String(hallId))
    || selectedHall
  );

  const resolveHallForNewBooking = async ({
    eventDate,
    endDate = null,
    startTime,
    endTime,
    preferredHall,
  }) => {
    const resolvedEndDate = endDate || eventDate;
    const timelineBookings = collectTimelineBookings(data?.timeline);
    const preferredConflict = findHallBookingConflict({
      bookings: timelineBookings,
      hallId: preferredHall.id,
      eventDate,
      endDate: resolvedEndDate,
      startTime,
      endTime,
    });
    if (!preferredConflict) {
      return preferredHall;
    }

    const dayBookings = await loadDayBookings(eventDate, resolvedEndDate);
    const alternateHall = findFirstAvailableHall({
      halls: bookableHalls,
      bookings: dayBookings,
      eventDate,
      endDate: resolvedEndDate,
      startTime,
      endTime,
      busyHallId: preferredHall.id,
      isHallBookable,
    });

    if (alternateHall) {
      return alternateHall;
    }

    return preferredHall;
  };

  const createHallRecord = async (payload) => {
    setSavingHall(true);
    setError('');
    try {
      const hall = await createHall(payload);
      setAddOpen(false);
      setSelectedHallId(String(hall.id));
      showMessage('success', 'Hall saved successfully');
      await loadDashboard();
      if (tab === 3) await loadManagementHalls();
    } catch (err) {
      showMessage('error', formatHallSaveError(err));
    } finally {
      setSavingHall(false);
    }
  };

  const resolveBookingSchedule = (hall, slotHour, bookingType = 'hourly', eventDate = selectedDate) => {
    if (bookingType === 'day') {
      const endDate = moment(eventDate, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
      return {
        bookingType: 'day',
        eventDate,
        endDate,
        startTime: FULL_DAY_DEFAULT_START_TIME,
        endTime: FULL_DAY_DEFAULT_END_TIME,
        duration_hours: 24,
        duration_minutes: 0,
        amount: computeFullDayAmount(hall, eventDate, endDate),
      };
    }
    const startTime = slotHour || '10:00';
    const durationHours = DEFAULT_HOURLY_DURATION_HOURS;
    const durationMinutes = 0;
    const computedEnd = computeEndFromDuration(eventDate, startTime, durationHours, durationMinutes);
    return {
      bookingType: 'hourly',
      eventDate,
      endDate: computedEnd.end_date,
      startTime,
      endTime: computedEnd.end_time,
      duration_hours: durationHours,
      duration_minutes: durationMinutes,
      amount: computeHourlyAmount(hall, durationHours, durationMinutes),
    };
  };

  const openBookingSlot = async (slotHour, options = {}) => {
    if (!selectedHall) {
      showMessage('error', 'Please add or select an active hall before creating a booking.');
      return;
    }
    if (!isHallBookable(selectedHall)) {
      showMessage('error', 'This hall is inactive and cannot accept new bookings.');
      return;
    }
    const {
      openDrawer = true,
      resetRange = false,
      eventDate,
      bookingType = 'hourly',
    } = options;
    const bookingDate = eventDate || selectedDate;
    if (eventDate && eventDate !== selectedDate) {
      setSelectedDate(eventDate);
    }

    if (
      bookingType === 'hourly'
      && !resetRange
      && activeSlotHour
      && bookingForm.start_time
      && bookingForm.booking_type !== 'day'
    ) {
      const clickedStart = timeToMinutes(slotHour);
      const currentStart = timeToMinutes(bookingForm.start_time);
      if (clickedStart >= currentStart) {
        const nextEnd = getRangeEndForSlot(slotHour);
        const targetHall = await resolveHallForNewBooking({
          eventDate: bookingDate,
          endDate: bookingDate,
          startTime: bookingForm.start_time,
          endTime: nextEnd,
          preferredHall: selectedHall,
        });
        const duration = durationFromStartEnd(
          bookingDate,
          bookingDate,
          bookingForm.start_time,
          nextEnd,
        );
        setBookingForm((prev) => withAutoBookingAmount(prev, {
          hall_id: String(targetHall.id),
          event_date: bookingDate,
          end_date: bookingDate,
          booking_type: 'hourly',
          end_time: nextEnd,
          duration_hours: duration.duration_hours,
          duration_minutes: duration.duration_minutes,
        }, targetHall));
        if (openDrawer) setBookingDrawerOpen(true);
        return;
      }
    }

    const draftSchedule = resolveBookingSchedule(selectedHall, slotHour, bookingType, bookingDate);
    const targetHall = await resolveHallForNewBooking({
      eventDate: bookingDate,
      endDate: draftSchedule.endDate || bookingDate,
      startTime: draftSchedule.startTime,
      endTime: draftSchedule.endTime,
      preferredHall: selectedHall,
    });
    const schedule = resolveBookingSchedule(targetHall, slotHour, bookingType, bookingDate);

    setActiveSlotHour(schedule.startTime);
    setBookingDrawerMode('create');
    setEditingBookingId(null);
    guestLinkedCustomerRef.current = null;
    setBookingForm({
      ...defaultBookingForm,
      hall_id: String(targetHall.id),
      event_date: schedule.eventDate,
      end_date: schedule.endDate,
      booking_type: schedule.bookingType,
      start_time: schedule.startTime,
      end_time: schedule.endTime,
      duration_hours: schedule.duration_hours,
      duration_minutes: schedule.duration_minutes,
      guests: targetHall.capacity || '',
      adults: targetHall.capacity || '',
      children: '',
      amount: schedule.amount,
    });
    if (openDrawer) setBookingDrawerOpen(true);
  };

  const applyBookingTypeChange = (nextType) => {
    const hall = resolveBookingHall(bookingForm.hall_id);
    if (!hall) return;
    const schedule = resolveBookingSchedule(
      hall,
      bookingForm.start_time || activeSlotHour || '10:00',
      nextType,
      bookingForm.event_date || selectedDate,
    );
    setBookingForm((prev) => withAutoBookingAmount(prev, {
      booking_type: schedule.bookingType,
      event_date: schedule.eventDate,
      end_date: schedule.endDate,
      start_time: schedule.startTime,
      end_time: schedule.endTime,
      duration_hours: schedule.duration_hours,
      duration_minutes: schedule.duration_minutes,
      amount: schedule.amount,
    }, hall));
    setActiveSlotHour(schedule.startTime);
  };

  const applyHourlyDurationChange = (nextHours, nextMinutes) => {
    const hall = resolveBookingHall(bookingForm.hall_id);
    const hours = nextHours ?? bookingForm.duration_hours ?? DEFAULT_HOURLY_DURATION_HOURS;
    const minutes = nextMinutes ?? bookingForm.duration_minutes ?? 0;
    const computedEnd = computeEndFromDuration(
      bookingForm.event_date || selectedDate,
      bookingForm.start_time || '10:00',
      hours,
      minutes,
    );
    setBookingForm((prev) => withAutoBookingAmount(prev, {
      duration_hours: hours,
      duration_minutes: minutes,
      end_date: computedEnd.end_date,
      end_time: computedEnd.end_time,
    }, hall));
  };

  const applyHourlyStartChange = (nextDate, nextStartTime) => {
    const hall = resolveBookingHall(bookingForm.hall_id);
    const eventDate = nextDate || bookingForm.event_date || selectedDate;
    const startTime = nextStartTime || bookingForm.start_time || '10:00';
    const hours = bookingForm.duration_hours ?? DEFAULT_HOURLY_DURATION_HOURS;
    const minutes = bookingForm.duration_minutes ?? 0;
    const computedEnd = computeEndFromDuration(eventDate, startTime, hours, minutes);
    setBookingForm((prev) => withAutoBookingAmount(prev, {
      event_date: eventDate,
      start_time: startTime,
      end_date: computedEnd.end_date,
      end_time: computedEnd.end_time,
    }, hall));
    setActiveSlotHour(startTime);
  };

  const openBookingForModify = (event, mode = 'modify') => {
    const parsedNotes = parseHallEventBookingNotes(event?.notes);
    const hallId = resolveHallBookingHallId(event, data?.halls || [], selectedHallId);
    const assignedHall = halls.find((hall) => String(hall.id) === String(hallId));
    const guestCount = String(event?.guests ?? '');
    let resolvedHallId = hallId;
    if (mode !== 'view' && assignedHall && !isHallBookable(assignedHall)) {
      resolvedHallId = bookableHalls[0] ? String(bookableHalls[0].id) : '';
      if (resolvedHallId) {
        showMessage(
          'info',
          `"${assignedHall.name}" is inactive. Select an active hall before saving.`,
        );
      }
    }

    setActiveSlotHour(event?.start_time || '');
    setBookingDrawerMode(mode);
    setEditingBookingId(event?.id || null);
    setEventAuditDrawerOpen(false);
    setEventAuditEvents([]);
    const existingGuestId = Number(event?.customer_guest_id);
    guestLinkedCustomerRef.current = (
      Number.isInteger(existingGuestId) && existingGuestId > 0
        ? {
          id: existingGuestId,
          name: event.customer_name || event.customer || '',
          email: event.customer_email || '',
        }
        : null
    );
    setBookingDrawerOpen(true);
    if (mode === 'view' && event?.id) {
      loadEventAudit(event.id);
    }
    const phoneParts = splitPhoneWithDialCode(event.customer_phone || '');
    const eventDate = event.event_date || event.date || selectedDate;
    const endDate = event.end_date || eventDate;
    const duration = durationFromStartEnd(eventDate, endDate, event.start_time, event.end_time);
    const totalDurationMinutes = (duration.duration_hours * 60) + duration.duration_minutes;
    const inferredBookingType = parsedNotes.booking_type
      || (totalDurationMinutes >= 10 * 60 || endDate !== eventDate ? 'day' : 'hourly');
    setBookingForm({
      ...defaultBookingForm,
      ...parsedNotes,
      hall_id: resolvedHallId,
      event_id: event.event_id || event.booking_code || (event.id ? `EVT-${event.id}` : ''),
      event_name: event.event_name || '',
      customer_name: event.customer_name || event.customer || '',
      customer_phone: phoneParts.nationalNumber,
      customer_phone_country_code: phoneParts.dialCode,
      customer_email: event.customer_email || '',
      event_date: eventDate,
      end_date: endDate,
      booking_type: inferredBookingType === 'day' ? 'day' : 'hourly',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      duration_hours: Math.min(
        MAX_HOURLY_DURATION_HOURS,
        Math.max(MIN_HOURLY_DURATION_HOURS, duration.duration_hours || DEFAULT_HOURLY_DURATION_HOURS),
      ),
      duration_minutes: 0,
      guests: guestCount,
      adults: parsedNotes.adults || guestCount,
      amount: String(event.amount ?? ''),
      status: event.status || 'tentative',
      payment_type: inferHallEventPaymentType(parsedNotes, event.amount),
      advance_amount: parsedNotes.advance_amount || '',
      payment_mode: parsedNotes.payment_mode || 'cash',
    });
  };

  const openBookingFromEvent = (event) => {
    if (!selectedHall && !event?.hall_id && !event?.hall) return;
    openBookingForModify(event);
  };

  const updateBookingField = (field) => (event) => {
    const value = event.target.value;
    setBookingForm((prev) => {
      if (field === 'hall_id') {
        const hall = resolveBookingHall(value);
        return withAutoBookingAmount(prev, {
          hall_id: value,
          guests: prev.guests || hall?.capacity || '',
          adults: prev.adults || hall?.capacity || '',
        }, hall);
      }
      const next = { ...prev, [field]: value };
      if (field === 'amount') {
        return syncHallEventAdvanceWithAmount(next);
      }
      return next;
    });
  };

  const openDeleteEventDialog = (row) => {
    if (!row?.id) return;
    setDeleteEventConfirmInput('');
    setDeleteEventDialog({ open: true, event: row });
  };

  const closeDeleteEventDialog = () => {
    if (deletingBookingId) return;
    setDeleteEventDialog({ open: false, event: null });
    setDeleteEventConfirmInput('');
  };

  const deleteEventConfirmValid = (
    deleteEventConfirmInput.trim().toLowerCase() === EVENT_DELETE_CONFIRM_WORD
  );

  const handleConfirmDeleteEvent = async () => {
    const row = deleteEventDialog.event;
    if (!row?.id || !deleteEventConfirmValid) return;

    setDeletingBookingId(row.id);
    try {
      await deleteHallBooking(row.id);
      if (editingBookingId === row.id) {
        closeBookingDrawer();
      }
      setDeleteEventDialog({ open: false, event: null });
      setDeleteEventConfirmInput('');
      showMessage('success', 'Hall event deleted successfully');
      setCalendarRefreshKey((key) => key + 1);
      await Promise.all([loadDashboard(), loadEventRows()]);
    } catch (err) {
      showMessage('error', err?.userMessage || err?.message || 'Failed to delete hall event.');
    } finally {
      setDeletingBookingId(null);
    }
  };

  const saveBookingRecord = async () => {
    if (!selectedHall && !bookingForm.hall_id) {
      showMessage('error', 'Please select a hall before saving the booking.');
      return;
    }
    const targetHallId = Number(bookingForm.hall_id || selectedHall?.id || 0);
    if (!targetHallId) {
      showMessage('error', 'Please select a hall before saving the booking.');
      return;
    }
    const targetHall = halls.find((hall) => String(hall.id) === String(targetHallId));
    if (!isHallBookable(targetHall)) {
      showMessage('error', 'This hall is inactive and cannot accept new bookings.');
      return;
    }

    let syncedForm = syncHallEventAdvanceWithAmount(bookingForm);
    if (syncedForm.booking_type === 'hourly') {
      const computedEnd = computeEndFromDuration(
        syncedForm.event_date,
        syncedForm.start_time,
        syncedForm.duration_hours,
        syncedForm.duration_minutes,
      );
      syncedForm = {
        ...syncedForm,
        end_date: computedEnd.end_date,
        end_time: computedEnd.end_time,
      };
    } else {
      syncedForm = {
        ...syncedForm,
        end_date: syncedForm.end_date || syncedForm.event_date,
      };
    }

    if (!isBookingPhoneValid(syncedForm.customer_phone, syncedForm.customer_phone_country_code)) {
      showMessage('error', 'Please enter a valid phone number.');
      return;
    }
    const startMoment = moment(`${syncedForm.event_date} ${syncedForm.start_time}`, 'YYYY-MM-DD HH:mm');
    const endMoment = moment(
      `${syncedForm.end_date || syncedForm.event_date} ${syncedForm.end_time}`,
      'YYYY-MM-DD HH:mm',
    );
    if (!startMoment.isValid() || !endMoment.isValid() || !endMoment.isAfter(startMoment)) {
      showMessage('error', 'End date/time must be after start date/time.');
      return;
    }

    // Always re-fetch overlapping bookings for this hall before save (authoritative client check).
    let bookingsForCheck = [];
    try {
      const payload = await getHallBookings({
        hall_id: targetHallId,
        start_date: syncedForm.event_date,
        end_date: syncedForm.end_date || syncedForm.event_date,
        page_size: 200,
      });
      bookingsForCheck = payload?.results || [];
    } catch {
      bookingsForCheck = [
        ...hallDayBookings,
        ...allDayBookings,
        ...collectTimelineBookings(data?.timeline),
      ];
    }

    const conflict = checkBookingTimeConflict({
      hallId: targetHallId,
      eventDate: syncedForm.event_date,
      endDate: syncedForm.end_date || syncedForm.event_date,
      startTime: syncedForm.start_time,
      endTime: syncedForm.end_time,
      excludeBookingId: bookingDrawerMode === 'modify' ? editingBookingId : null,
      bookings: bookingsForCheck,
    });
    if (conflict) {
      showMessage('error', formatHallOverlapErrorMessage(conflict, targetHall?.name || 'This hall'));
      return;
    }

    setSavingBooking(true);
    try {
      const linkedGuestId = Number(guestLinkedCustomerRef.current?.id);
      const payload = {
        hall: targetHallId,
        event_name: syncedForm.event_name,
        customer_name: syncedForm.customer_name,
        customer_phone: formatPhoneWithDialCode(
          syncedForm.customer_phone_country_code,
          syncedForm.customer_phone
        ),
        customer_email: syncedForm.customer_email,
        event_date: syncedForm.event_date,
        end_date: syncedForm.end_date || syncedForm.event_date,
        start_time: syncedForm.start_time,
        end_time: syncedForm.end_time,
        guests: Number(syncedForm.guests || 0),
        amount: Number(syncedForm.amount || 0),
        status: syncedForm.status,
        notes: buildHallEventBookingNotes(syncedForm),
      };
      if (Number.isInteger(linkedGuestId) && linkedGuestId > 0) {
        payload.customer_guest_id = linkedGuestId;
      }

      if (bookingDrawerMode === 'modify' && editingBookingId) {
        await updateHallBooking(editingBookingId, payload);
      } else {
        await createHallBooking(payload);
      }

      closeBookingDrawer();
      showMessage('success', bookingDrawerMode === 'modify' ? 'Hall booking updated successfully' : 'Hall booking created successfully');
      setCalendarRefreshKey((key) => key + 1);
      if (bookingDrawerMode === 'create' && bookingForm.event_date && bookingForm.event_date !== selectedDate) {
        setSelectedDate(bookingForm.event_date);
      } else {
        await Promise.all([loadDashboard(), loadEventRows()]);
      }
    } catch (err) {
      const apiData = err?.response?.data || err?.data || {};
      const overlapMessage = (
        (typeof apiData.time === 'string' && apiData.time)
        || (Array.isArray(apiData.time) && apiData.time[0])
        || ''
      );
      showMessage(
        'error',
        overlapMessage
          || err?.userMessage
          || err?.message
          || `Failed to ${bookingDrawerMode === 'modify' ? 'update' : 'create'} hall booking.`,
      );
    } finally {
      setSavingBooking(false);
    }
  };

  const analytics = data?.analytics || {};
  const analyticsCharts = analytics.charts || {};
  const analyticsChartData = useMemo(() => ({
    daily_booking_trend: normalizeChartRows(analyticsCharts.daily_booking_trend),
    weekly_occupancy: normalizeChartRows(analyticsCharts.weekly_occupancy),
    monthly_revenue: normalizeChartRows(analyticsCharts.monthly_revenue),
    popular_halls: analyticsCharts.popular_halls || [],
    booking_status_distribution: analyticsCharts.booking_status_distribution || [],
    money_overview: normalizeChartRows(analyticsCharts.money_overview),
    event_funnel: normalizeChartRows(analyticsCharts.event_funnel),
  }), [analyticsCharts]);
  const bookingDrawerHallName = halls.find((hall) => String(hall.id) === String(bookingForm.hall_id))?.name
    || selectedHall?.name
    || 'Select hall';
  const isBookingDrawerReadOnly = bookingDrawerMode === 'view';
  const hallDatePickerSlotProps = useMemo(
    () => getHallDatePickerSlotProps(isDark, {
      required: true,
      disabled: isBookingDrawerReadOnly,
    }),
    [isDark, isBookingDrawerReadOnly],
  );
  useEffect(() => {
    setActiveSlotHour('');
    setBookingDrawerOpen(false);
    setBookingDrawerMode('create');
    setEditingBookingId(null);
    guestLinkedCustomerRef.current = null;
    setBookingForm(defaultBookingForm);
  }, [selectedDate, selectedHallId]);

  useEffect(() => {
    if (!bookableHalls.length) return;
    const currentIsBookable = bookableHalls.some((hall) => String(hall.id) === String(selectedHallId));
    if (!currentIsBookable) {
      setSelectedHallId(String(bookableHalls[0].id));
    }
  }, [bookableHalls, selectedHallId]);

  useEffect(() => {
    if (!bookingDrawerOpen || bookingDrawerMode !== 'create' || !bookingForm.hall_id) {
      return;
    }
    const selectedBookingHall = halls.find(
      (hall) => String(hall.id) === String(bookingForm.hall_id),
    );
    if (selectedBookingHall && !isHallBookable(selectedBookingHall) && bookableHalls.length) {
      const fallbackHall = bookableHalls[0];
      setBookingForm((prev) => withAutoBookingAmount(prev, {
        hall_id: String(fallbackHall.id),
      }, fallbackHall));
    }
  }, [
    bookableHalls,
    bookingDrawerMode,
    bookingDrawerOpen,
    bookingForm.hall_id,
    halls,
  ]);

  const analyticsRangeLabel = useMemo(() => {
    const presetLabels = {
      today: 'Today',
      yesterday: 'Yesterday',
      last_7_days: 'Last 7 Days',
      last_30_days: 'Last 30 Days',
      this_month: 'This Month',
      last_month: 'Last Month',
    };
    return presetLabels[analyticsPreset] || 'Period';
  }, [analyticsPreset]);

  const handleEventsFilterChange = (field, value) => {
    setEventsPage(1);
    setEventsFilters((prev) => ({ ...prev, [field]: value }));
  };

  const eventListColumns = [
    {
      field: 'event_id',
      label: 'Event ID',
      sortable: false,
      render: (_value, row) => renderEventIdLink(row, navigate),
    },
    {
      field: 'event_name',
      label: 'Event',
      sortable: false,
      render: (_value, row) => (
        <Stack spacing={0.3}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: 'text.primary' }}>
            {row.event_name || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {row.customer_name || row.customer || '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'hall_name',
      label: 'Hall',
      sortable: false,
      render: (_value, row) => row.hall_name || row.hall || '—',
    },
    {
      field: 'event_date',
      label: 'Date',
      sortable: false,
      render: (_value, row) => {
        const dateValue = row.event_date || row.date;
        if (!dateValue) return '—';
        const parsed = moment(dateValue);
        return parsed.isValid() ? parsed.format('DD MMM YYYY') : dateValue;
      },
    },
    {
      field: 'time',
      label: 'Time',
      sortable: false,
      render: (_value, row) => (
        row.start_time && row.end_time
          ? `${row.start_time} - ${row.end_time}`
          : (row.time || '—')
      ),
    },
    {
      field: 'guests',
      label: 'Guests',
      align: 'right',
      sortable: false,
      render: (value) => numberFormat.format(Number(value || 0)),
    },
    {
      field: 'amount',
      label: 'Amount',
      align: 'right',
      sortable: false,
      render: (value) => formatCurrency(value || 0),
    },
    {
      field: 'status',
      label: 'Status',
      sortable: false,
      render: (value) => (
        <Chip
          size="small"
          label={value || 'tentative'}
          sx={statusChipSx(value || 'tentative', isDark)}
        />
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      render: (_value, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              openBookingForModify(row, 'view');
            }}
            sx={{ color: SECONDARY }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              openBookingForModify(row, 'modify');
            }}
            sx={{ color: HALL_ACCENT }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              openDeleteEventDialog(row);
            }}
            disabled={deletingBookingId === row.id}
            sx={{ color: DANGER }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const eventListFilterRow = eventListColumns.map((column) => {
    switch (column.field) {
      case 'event_id':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Event ID"
            placeholder="EVT-2026-..."
            value={eventsFilters.event_id}
            onChange={(event) => handleEventsFilterChange('event_id', event.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'event_name':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Event / Guest"
            placeholder="Search..."
            value={eventsFilters.search}
            onChange={(event) => handleEventsFilterChange('search', event.target.value)}
            sx={{ minWidth: 140 }}
          />
        );
      case 'hall_name':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={eventsFilters.hall_id}
            onChange={(event) => handleEventsFilterChange('hall_id', event.target.value)}
            displayEmpty
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All halls</MenuItem>
            {halls.map((hall) => (
              <MenuItem key={hall.id} value={hall.id}>{hall.name}</MenuItem>
            ))}
          </FormSelect>
        );
      case 'event_date':
        return (
          <FormDatePicker
            key={column.field}
            label="Date"
            format="DD/MM/YYYY"
            value={eventsFilters.date ? moment(eventsFilters.date, 'YYYY-MM-DD') : null}
            onChange={(nextDate) => {
              handleEventsFilterChange(
                'date',
                nextDate?.isValid?.() ? nextDate.format('YYYY-MM-DD') : '',
              );
            }}
            sx={{ minWidth: 140 }}
          />
        );
      case 'status':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={eventsFilters.status}
            onChange={(event) => handleEventsFilterChange('status', event.target.value)}
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="tentative">Tentative</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </FormSelect>
        );
      default:
        return <div key={column.field} />;
    }
  });

  const surfaceSx = {
    p: { xs: 2, md: 2.5 },
    borderRadius: 3,
    background: tokens.surfaceGradient,
    border: `1px solid ${tokens.surfaceBorder}`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : `0 16px 40px ${alpha('#0f172a', 0.08)}`,
  };

  const insightPaperSx = {
    ...surfaceSx,
    p: { xs: 1.5, md: 2 },
    borderRadius: 2.5,
  };

  const listEventsPanel = (
    <Paper sx={surfaceSx}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ color: tokens.text, fontWeight: 900, letterSpacing: '-0.01em' }}>
            Event List
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.subtleText }}>
            All hall events with filters and pagination.
          </Typography>
        </Box>

        <DataTable
          columns={eventListColumns}
          data={eventRows}
          loading={eventsLoading}
          pagination={{
            page: Math.max(eventsPage - 1, 0),
            perPage: eventsPerPage,
            totalRecords: eventsCount,
          }}
          onPageChange={(page) => setEventsPage(page + 1)}
          onPerPageChange={(perPage) => {
            setEventsPerPage(Number(perPage || 10));
            setEventsPage(1);
          }}
          emptyMessage="No events found for selected filters."
          stickyHeader
          filterRow={eventListFilterRow}
          onRowClick={(row) => openBookingForModify(row, 'view')}
        />
      </Stack>
    </Paper>
  );

  const bookingCalendarPanel = (
    <HallBookingCalendar
      isDark={isDark}
      tokens={tokens}
      surfaceSx={surfaceSx}
      halls={halls}
      selectedHallId={calendarHallFilter}
      onSelectHall={(hallId) => {
        setCalendarHallFilter(hallId || '');
        if (hallId) setSelectedHallId(String(hallId));
      }}
      selectedDate={selectedDate}
      onSelectDate={setSelectedDate}
      formatCurrency={formatCurrency}
      refreshKey={calendarRefreshKey}
      onAddHall={() => setAddOpen(true)}
      onOpenBookingForDate={(dateStr, hour, options = {}) => {
        openBookingSlot(hour || '10:00', {
          eventDate: dateStr,
          openDrawer: true,
          resetRange: true,
          bookingType: options.bookingType || 'hourly',
        });
      }}
      onOpenBookingEvent={(event, mode = 'view') => {
        openBookingForModify(event, mode);
      }}
    />
  );

  const bookingDrawer = (
    <Drawer
      anchor="right"
      open={bookingDrawerOpen}
      onClose={closeBookingDrawer}
      ModalProps={{
        keepMounted: true,
        disableRestoreFocus: true,
        disableEnforceFocus: true,
      }}
      PaperProps={{
        sx: {
          ...HALL_SIDE_DRAWER_WIDTH_SX,
          display: 'flex',
          flexDirection: 'column',
          background: isDark
            ? `linear-gradient(165deg, #07111f 0%, #0b1a2a 55%, #0a1f1a 100%)`
            : `linear-gradient(165deg, #ffffff 0%, #f7fbff 55%, #f3fbf7 100%)`,
          borderLeft: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
          boxShadow: isDark
            ? `-18px 0 48px ${alpha('#000', 0.45)}`
            : `-16px 0 40px ${alpha('#0f172a', 0.12)}`,
        },
      }}
    >
      <Box
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onMouseUp={(event) => event.stopPropagation()}
      >
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            px: 1.5,
            py: 1.2,
            borderBottom: `1px solid ${alpha(tokens.text, 0.1)}`,
            boxSizing: 'border-box',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `
                radial-gradient(circle at 12% 0%, ${alpha(HALL_ACCENT, isDark ? 0.22 : 0.16)} 0%, transparent 42%),
                radial-gradient(circle at 90% 20%, ${alpha('#5b9cf5', isDark ? 0.18 : 0.12)} 0%, transparent 36%)
              `,
              pointerEvents: 'none',
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  color: tokens.text,
                  fontWeight: 950,
                  fontSize: '1.05rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                {bookingDrawerMode === 'view'
                  ? 'Event Overview'
                  : bookingDrawerMode === 'modify'
                    ? 'Edit Booking'
                    : 'New Booking'}
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  color: tokens.subtleText,
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {bookingDrawerHallName}
                {' · '}
                {moment(bookingForm.event_date || selectedDate, 'YYYY-MM-DD').format('DD MMM YYYY')}
                {bookingForm.booking_type === 'day' && bookingForm.end_date && bookingForm.end_date !== bookingForm.event_date
                  ? ` → ${moment(bookingForm.end_date, 'YYYY-MM-DD').format('DD MMM YYYY')}`
                  : ''}
              </Typography>
            </Box>
            {isBookingDrawerReadOnly ? (
              <Chip
                size="small"
                icon={<FactCheckIcon sx={{ fontSize: '16px !important' }} />}
                label={`Audit${eventAuditEvents.length ? ` · ${eventAuditEvents.length}` : ''}`}
                onClick={openEventAuditDrawer}
                disabled={!editingBookingId}
                sx={{
                  height: 32,
                  fontWeight: 800,
                  cursor: editingBookingId ? 'pointer' : 'default',
                  color: isDark ? HALL_ACCENT : '#0f766e',
                  bgcolor: isDark ? alpha(HALL_ACCENT, 0.16) : alpha(HALL_ACCENT, 0.14),
                  border: `1px solid ${alpha(HALL_ACCENT, 0.4)}`,
                  '& .MuiChip-icon': { color: 'inherit' },
                  '&:hover': {
                    bgcolor: isDark ? alpha(HALL_ACCENT, 0.24) : alpha(HALL_ACCENT, 0.22),
                  },
                }}
              />
            ) : (
              <IconButton
                size="small"
                onClick={closeBookingDrawer}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '12px',
                  color: tokens.subtleText,
                  bgcolor: alpha(tokens.text, isDark ? 0.06 : 0.04),
                  border: `1px solid ${alpha(tokens.text, 0.1)}`,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>

        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box
            ref={bookingDrawerScrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 1.5,
              boxSizing: 'border-box',
            }}
          >
            <Stack spacing={1.5}>
              {isBookingDrawerReadOnly ? (
                <HallBookingViewSummary
                  form={bookingForm}
                  hallName={bookingDrawerHallName}
                  eventId={bookingForm.event_id}
                  isDark={isDark}
                  tokens={tokens}
                  formatCurrency={formatCurrency}
                />
              ) : null}
              {!isBookingDrawerReadOnly && bookingOverlapMessage ? (
                <Alert
                  severity="warning"
                  action={alternateHallForSlot ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => switchBookingToHall(alternateHallForSlot)}
                      sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}
                    >
                      Use {alternateHallForSlot.name}
                    </Button>
                  ) : null}
                >
                  {bookingOverlapMessage}
                  {alternateHallForSlot
                    ? ` Tap "Use ${alternateHallForSlot.name}" or choose another hall below.`
                    : ' Choose another hall below or adjust the time.'}
                </Alert>
              ) : null}
              {!isBookingDrawerReadOnly ? (
              <Grid
                container
                columnSpacing={HALL_DRAWER_FIELD_GAP}
                rowSpacing={HALL_DRAWER_FIELD_GAP}
                sx={getHallDrawerFormGridSx(isDark)}
              >
                <Grid item xs={12}>
                  <HallPickerCards
                    halls={bookableHalls}
                    value={bookingForm.hall_id}
                    isDark={isDark}
                    disabled={isBookingDrawerReadOnly}
                    helperText={
                      bookableHalls.length
                        ? 'Select one hall for this booking. Only active halls can be booked.'
                        : undefined
                    }
                    onChange={(hallId) => {
                      updateBookingField('hall_id')({ target: { value: hallId } });
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Event Name"
                    value={bookingForm.event_name}
                    onChange={updateBookingField('event_name')}
                    size="small"
                    fullWidth
                    required
                    disabled={isBookingDrawerReadOnly}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Event Type"
                    value={bookingForm.event_type}
                    onChange={updateBookingField('event_type')}
                    size="small"
                    fullWidth
                    disabled={isBookingDrawerReadOnly}
                  >
                    <MenuItem value="wedding">Wedding</MenuItem>
                    <MenuItem value="engagement">Engagement</MenuItem>
                    <MenuItem value="reception">Reception</MenuItem>
                    <MenuItem value="sangeet">Sangeet / Haldi</MenuItem>
                    <MenuItem value="birthday">Birthday / Party</MenuItem>
                    <MenuItem value="conference">Conference</MenuItem>
                    <MenuItem value="seminar">Seminar / Training</MenuItem>
                    <MenuItem value="exhibition">Exhibition</MenuItem>
                    <MenuItem value="corporate">Corporate Event</MenuItem>
                    <MenuItem value="social">Social Gathering</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <HallEventGuestPhoneField
                    phone={bookingForm.customer_phone}
                    countryCode={bookingForm.customer_phone_country_code}
                    onPhoneChange={handleCustomerPhoneChange}
                    onCountryCodeChange={(nextCountryCode) => {
                      setBookingForm((prev) => ({ ...prev, customer_phone_country_code: nextCountryCode }));
                    }}
                    onGuestSelect={handleGuestSelectFromPhone}
                    disabled={isBookingDrawerReadOnly}
                    required={!isBookingDrawerReadOnly}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Host / Customer Name"
                    value={bookingForm.customer_name}
                    onChange={handleCustomerNameChange}
                    size="small"
                    fullWidth
                    required
                    disabled={isBookingDrawerReadOnly}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Email" value={bookingForm.customer_email} onChange={handleCustomerEmailChange} size="small" fullWidth disabled={isBookingDrawerReadOnly} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ width: '100%' }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1}>
                        {[
                          { value: 'hourly', label: 'Hourly' },
                          { value: 'day', label: 'Full Day' },
                        ].map((option) => {
                          const active = bookingForm.booking_type === option.value;
                          return (
                            <Button
                              key={option.value}
                              onClick={() => !isBookingDrawerReadOnly && applyBookingTypeChange(option.value)}
                              disabled={isBookingDrawerReadOnly}
                              sx={{
                                flex: 1,
                                textTransform: 'none',
                                fontWeight: 800,
                                borderRadius: '14px',
                                py: 1.1,
                                color: active ? '#031512' : (isDark ? '#aab2bc' : '#5a646e'),
                                bgcolor: active
                                  ? HALL_ACCENT
                                  : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)'),
                                border: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                  bgcolor: active
                                    ? alpha(HALL_ACCENT, 0.92)
                                    : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)'),
                                },
                              }}
                            >
                              {option.label}
                            </Button>
                          );
                        })}
                      </Stack>

                      {bookingForm.booking_type === 'hourly' ? (
                        <Stack spacing={0.85}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="stretch"
                            sx={{ width: '100%' }}
                          >
                            <Box sx={{ flex: '1 1 50%', width: '50%', minWidth: 0 }}>
                              <DateTimePicker
                                label="Start Date & Time"
                                ampm
                                format="DD/MM/YYYY hh:mm A"
                                value={
                                  bookingForm.event_date && bookingForm.start_time
                                    ? moment(`${bookingForm.event_date} ${bookingForm.start_time}`, 'YYYY-MM-DD HH:mm')
                                    : null
                                }
                                onChange={isBookingDrawerReadOnly ? undefined : (nextValue) => {
                                  if (!nextValue?.isValid?.()) return;
                                  applyHourlyStartChange(
                                    nextValue.format('YYYY-MM-DD'),
                                    nextValue.format('HH:mm'),
                                  );
                                }}
                                slotProps={hallDatePickerSlotProps}
                              />
                            </Box>

                            <Box
                              sx={{
                                flex: '1 1 50%',
                                width: '50%',
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                px: 1,
                                minHeight: 48,
                                borderRadius: '14px',
                                border: 'none',
                                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
                              }}
                            >
                              <IconButton
                                size="small"
                                disabled={
                                  isBookingDrawerReadOnly
                                  || Number(bookingForm.duration_hours || 0) <= MIN_HOURLY_DURATION_HOURS
                                }
                                onClick={() => applyHourlyDurationChange(
                                  Math.max(
                                    MIN_HOURLY_DURATION_HOURS,
                                    Number(bookingForm.duration_hours || DEFAULT_HOURLY_DURATION_HOURS) - 1,
                                  ),
                                  0,
                                )}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: '10px',
                                  color: isDark ? '#aab2bc' : '#5a646e',
                                  bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                                  border: 'none',
                                  '&:hover': { bgcolor: alpha(HALL_ACCENT, 0.16), color: HALL_ACCENT },
                                  '&.Mui-disabled': { opacity: 0.4 },
                                }}
                              >
                                <RemoveIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                              <Box sx={{ minWidth: 46, textAlign: 'center', px: 0.25 }}>
                                <Typography sx={{ color: tokens.text, fontWeight: 900, fontSize: '0.95rem', lineHeight: 1.1 }}>
                                  {Number(bookingForm.duration_hours || DEFAULT_HOURLY_DURATION_HOURS)}h
                                </Typography>
                                <Typography sx={{ color: tokens.subtleText, fontWeight: 700, fontSize: '0.62rem', lineHeight: 1.1 }}>
                                  Hours
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                disabled={
                                  isBookingDrawerReadOnly
                                  || Number(bookingForm.duration_hours || 0) >= MAX_HOURLY_DURATION_HOURS
                                }
                                onClick={() => applyHourlyDurationChange(
                                  Math.min(
                                    MAX_HOURLY_DURATION_HOURS,
                                    Number(bookingForm.duration_hours || DEFAULT_HOURLY_DURATION_HOURS) + 1,
                                  ),
                                  0,
                                )}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: '10px',
                                  color: '#031512',
                                  bgcolor: HALL_ACCENT,
                                  border: 'none',
                                  '&:hover': { bgcolor: alpha(HALL_ACCENT, 0.9) },
                                  '&.Mui-disabled': { opacity: 0.4, bgcolor: alpha(HALL_ACCENT, 0.35) },
                                }}
                              >
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          </Stack>

                          <Typography sx={{ color: tokens.subtleText, fontSize: '0.75rem', fontWeight: 700 }}>
                            Ends {moment(
                              `${bookingForm.end_date || bookingForm.event_date} ${bookingForm.end_time}`,
                              'YYYY-MM-DD HH:mm',
                            ).isValid()
                              ? moment(
                                `${bookingForm.end_date || bookingForm.event_date} ${bookingForm.end_time}`,
                                'YYYY-MM-DD HH:mm',
                              ).format('DD MMM, hh:mm A')
                              : '—'}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="stretch"
                          sx={{ width: '100%' }}
                        >
                          <Box sx={{ flex: '1 1 50%', width: '50%', minWidth: 0 }}>
                            <DateTimePicker
                              label="Start Date & Time"
                              ampm
                              format="DD/MM/YYYY hh:mm A"
                              value={
                                bookingForm.event_date && bookingForm.start_time
                                  ? moment(`${bookingForm.event_date} ${bookingForm.start_time}`, 'YYYY-MM-DD HH:mm')
                                  : null
                              }
                              onChange={isBookingDrawerReadOnly ? undefined : (nextValue) => {
                                if (!nextValue?.isValid?.()) return;
                                const nextDate = nextValue.format('YYYY-MM-DD');
                                const nextTime = nextValue.format('HH:mm');
                                const endMoment = nextValue.clone().add(24, 'hours');
                                const hall = resolveBookingHall(bookingForm.hall_id);
                                setBookingForm((prev) => withAutoBookingAmount(prev, {
                                  event_date: nextDate,
                                  start_time: nextTime,
                                  end_date: endMoment.format('YYYY-MM-DD'),
                                  end_time: endMoment.format('HH:mm'),
                                }, hall));
                                setActiveSlotHour(nextTime);
                              }}
                              slotProps={hallDatePickerSlotProps}
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 50%', width: '50%', minWidth: 0 }}>
                            <DateTimePicker
                              label="End Date & Time"
                              ampm
                              format="DD/MM/YYYY hh:mm A"
                              value={
                                (bookingForm.end_date || bookingForm.event_date) && bookingForm.end_time
                                  ? moment(
                                    `${bookingForm.end_date || bookingForm.event_date} ${bookingForm.end_time}`,
                                    'YYYY-MM-DD HH:mm',
                                  )
                                  : null
                              }
                              onChange={isBookingDrawerReadOnly ? undefined : (nextValue) => {
                                if (!nextValue?.isValid?.()) return;
                                const nextDate = nextValue.format('YYYY-MM-DD');
                                const nextTime = nextValue.format('HH:mm');
                                const hall = resolveBookingHall(bookingForm.hall_id);
                                setBookingForm((prev) => withAutoBookingAmount(prev, {
                                  end_date: nextDate,
                                  end_time: nextTime,
                                }, hall));
                              }}
                              slotProps={hallDatePickerSlotProps}
                            />
                          </Box>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Total Amount"
                    type="number"
                    value={bookingForm.amount}
                    onChange={updateBookingField('amount')}
                    size="small"
                    fullWidth
                    required
                    disabled={isBookingDrawerReadOnly}
                    helperText={getBookingAmountHelperText(
                      resolveBookingHall(bookingForm.hall_id),
                      bookingForm,
                    )}
                    InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField select label="Status" value={bookingForm.status} onChange={updateBookingField('status')} size="small" fullWidth disabled={isBookingDrawerReadOnly}>
                    <MenuItem value="tentative">Tentative</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Total Guests" type="number" value={bookingForm.guests} onChange={updateBookingField('guests')} size="small" fullWidth required disabled={isBookingDrawerReadOnly} />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Adults" type="number" value={bookingForm.adults} onChange={updateBookingField('adults')} size="small" fullWidth disabled={isBookingDrawerReadOnly} />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Children" type="number" value={bookingForm.children} onChange={updateBookingField('children')} size="small" fullWidth disabled={isBookingDrawerReadOnly} />
                </Grid>
                <HallEventPaymentFields
                  paymentType={bookingForm.payment_type}
                  paymentMode={bookingForm.payment_mode}
                  advanceAmount={bookingForm.advance_amount}
                  totalAmount={bookingForm.amount}
                  onPaymentTypeChange={(nextType) => {
                    setBookingForm((prev) => applyHallEventPaymentTypeChange(prev, nextType));
                  }}
                  onPaymentModeChange={(nextMode) => {
                    setBookingForm((prev) => ({ ...prev, payment_mode: nextMode }));
                  }}
                  onAdvanceAmountChange={(nextAdvance) => {
                    setBookingForm((prev) => ({ ...prev, advance_amount: nextAdvance }));
                  }}
                  isDark={isDark}
                  readOnly={isBookingDrawerReadOnly}
                  subtleText={tokens.subtleText}
                />
                <Grid item xs={12}>
                  <TextField
                    label="Special Requirements / Notes"
                    value={bookingForm.notes}
                    onChange={updateBookingField('notes')}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={isBookingDrawerReadOnly}
                    placeholder="Agenda, setup, chairs, sound, AC, parking, rooms, kitchen or event instructions"
                  />
                </Grid>
              </Grid>
              ) : null}
            </Stack>
          </Box>
        </LocalizationProvider>
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${alpha(tokens.text, 0.1)}`,
            display: 'grid',
            gridTemplateColumns: isBookingDrawerReadOnly ? '1fr 1.35fr' : '1fr 1fr',
            gap: 1,
            boxSizing: 'border-box',
            background: isDark ? alpha('#000', 0.18) : alpha('#fff', 0.72),
            backdropFilter: 'blur(8px)',
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            disabled={savingBooking}
            onClick={closeBookingDrawer}
            sx={{
              borderRadius: '14px',
              borderColor: alpha(tokens.text, 0.16),
              color: tokens.text,
              fontWeight: 900,
              textTransform: 'none',
              py: 1.05,
            }}
          >
            {isBookingDrawerReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {isBookingDrawerReadOnly ? (
            <Button
              variant="contained"
              fullWidth
              disabled={!editingBookingId}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                if (!editingBookingId) return;
                closeBookingDrawer();
                navigate(`/hall/event/${editingBookingId}/`);
              }}
              sx={{
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 950,
                py: 1.05,
                color: '#031512',
                background: `linear-gradient(135deg, ${HALL_ACCENT} 0%, #35c9a0 100%)`,
                boxShadow: `0 12px 24px ${alpha(HALL_ACCENT, 0.32)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${HALL_ACCENT} 0%, #2fbe96 100%)`,
                  boxShadow: `0 14px 28px ${alpha(HALL_ACCENT, 0.4)}`,
                },
              }}
            >
              Detail Event
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              disabled={
                savingBooking
                || Boolean(bookingOverlapConflict)
                || !bookingForm.event_name.trim()
                || !bookingForm.customer_name.trim()
                || !isBookingPhoneValid(bookingForm.customer_phone, bookingForm.customer_phone_country_code)
                || !Number(bookingForm.guests || 0)
              }
              onClick={saveBookingRecord}
              sx={{
                borderRadius: '14px',
                bgcolor: HALL_ACCENT,
                color: '#031512',
                fontWeight: 950,
                textTransform: 'none',
                py: 1.05,
                boxShadow: `0 12px 24px ${alpha(HALL_ACCENT, 0.28)}`,
                '&:hover': { bgcolor: '#3bd1a7' },
              }}
            >
              {savingBooking ? 'Saving...' : bookingDrawerMode === 'modify' ? 'Save Changes' : 'Save Booking'}
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  );

  const eventAuditDrawer = (
    <HallAuditDrawer
      open={eventAuditDrawerOpen}
      onClose={() => setEventAuditDrawerOpen(false)}
      title="Event Audit"
      emptyMessage="No event activity recorded yet."
      hallName={bookingDrawerHallName}
      hallCode={bookingForm.event_id || (editingBookingId ? `EVT-${editingBookingId}` : '')}
      events={eventAuditEvents}
      isDark={isDark}
      loading={eventAuditLoading}
      onRefresh={() => loadEventAudit(editingBookingId)}
    />
  );

  return (
    <Box sx={getDashboardPageShellSx(themeMode)}>
      <Stack spacing={2.5} sx={{ width: '100%' }}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <HallModuleNav
          value={tab}
          onChange={handleTabChange}
          accentColor={HALL_ACCENT}
        />

        {tab === 0 ? (
          <HallAnalyticsOverview
            isDark={isDark}
            tokens={tokens}
            themeMode={themeMode}
            surfaceSx={surfaceSx}
            insightPaperSx={insightPaperSx}
            analytics={analytics}
            analyticsChartData={analyticsChartData}
            analyticsLoading={analyticsLoading}
            analyticsPdfCaptureMode={analyticsPdfCaptureMode}
            analyticsPdfExporting={analyticsPdfExporting}
            analyticsInsightsRef={analyticsInsightsRef}
            analyticsRangeLabel={analyticsRangeLabel}
            analyticsStartDate={analyticsStartDate}
            analyticsEndDate={analyticsEndDate}
            analyticsPreset={analyticsPreset}
            onDateRangeChange={handleAnalyticsDateRangeChange}
            onRefresh={handleAnalyticsRefresh}
            onPdfDownload={handleAnalyticsPdfDownload}
            formatCurrency={formatCurrency}
            upcomingEvents={data?.upcoming_events || []}
            statusChipSx={statusChipSx}
            renderEventIdLink={renderEventIdLink}
            navigate={navigate}
          />
        ) : tab === 1 ? (
          bookingCalendarPanel
        ) : tab === 2 ? (
          listEventsPanel
        ) : tab === 3 ? (
          <HallManagementWorkspace
            halls={managementHallsLoaded ? managementHalls : (managementHalls.length ? managementHalls : halls)}
            loading={managementHallsLoading && !(managementHalls.length || halls.length)}
            refreshing={managementHallsLoading && Boolean(managementHalls.length || halls.length)}
            isDark={isDark}
            tokens={tokens}
            surfaceSx={surfaceSx}
            formatCurrency={formatCurrency}
            onAddHall={() => setAddOpen(true)}
            onShowMessage={showMessage}
            onRefresh={async () => {
              await loadManagementHalls();
              await loadDashboard();
            }}
          />
        ) : (
          <HallVendorManagementWorkspace
            isDark={isDark}
            tokens={tokens}
            surfaceSx={surfaceSx}
            onShowMessage={showMessage}
          />
        )}
      </Stack>

      <AddHallDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={createHallRecord}
        saving={savingHall}
        isDark={isDark}
      />
      {bookingDrawer}
      {eventAuditDrawer}
      <Dialog
        open={deleteEventDialog.open}
        onClose={deletingBookingId ? undefined : closeDeleteEventDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#f8fafc' : '#0f172a',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>Delete Event</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDark ? '#cbd5e1' : '#475569', mb: 1.5 }}>
            Delete event{' '}
            <strong>
              "
              {deleteEventDialog.event?.event_name
                || deleteEventDialog.event?.event_id
                || deleteEventDialog.event?.booking_code
                || 'this event'}
              "
            </strong>
            ? This cannot be undone. Type <strong>confirm</strong> below to proceed.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label='Type "confirm"'
            value={deleteEventConfirmInput}
            onChange={(event) => setDeleteEventConfirmInput(event.target.value)}
            disabled={Boolean(deletingBookingId)}
            error={deleteEventConfirmInput.length > 0 && !deleteEventConfirmValid}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? alpha('#ffffff', 0.04) : '#ffffff',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}` }}>
          <Button
            onClick={closeDeleteEventDialog}
            disabled={Boolean(deletingBookingId)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteEvent}
            variant="contained"
            color="error"
            disabled={Boolean(deletingBookingId) || !deleteEventConfirmValid}
            startIcon={deletingBookingId ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {deletingBookingId ? 'Deleting...' : 'Delete Event'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ whiteSpace: 'pre-line' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
